/**
 * PREVIEW / MOCK-ONLY. Do NOT import from outside
 * `apps/carrier-dashboard/src/pages/MyOrders/preview/`. See
 * `./README.md` for the rules and the promotion path.
 */
import React, { useState } from 'react';
import { ChevronDownIcon } from '@hero/web-icons';
import type { MockOrder, MockOrderStatus, MockViewerRole } from './types';
import { ExpandedOrderRow } from './ExpandedOrderRow';

export interface MockOrdersTableProps {
  orders: MockOrder[];
  viewer: MockViewerRole;
}

const statusPill: Record<MockOrderStatus, { label: string; className: string }> =
  {
    pending: {
      label: 'Pending',
      className: 'bg-gray-100 text-gray-700 ring-gray-200',
    },
    searching: {
      label: 'Searching',
      className: 'bg-blue-50 text-blue-700 ring-blue-200',
    },
    assigned: {
      label: 'Assigned',
      className: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
    },
    in_transit: {
      label: 'In transit',
      className: 'bg-amber-50 text-amber-700 ring-amber-200',
    },
    delivered: {
      label: 'Delivered',
      className: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    },
  };

const formatMoney = (amount: number, currency: string) =>
  `${amount.toLocaleString('en-US')} ${currency}`;

const formatDate = (d: Date) =>
  d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * Pick the row-level "headline" amount: top-of-chain leg (what the
 * shipper or originating customer pays). Empty when the order has
 * no contracts yet.
 */
const pickHeadlineAmount = (order: MockOrder): string => {
  const first = order.legs[0];
  if (!first) return '—';
  return formatMoney(first.amount, first.currency);
};

/**
 * Counterparty label for the row's "to/from" cell. Reads the CRM
 * Client when present, otherwise the carrier, otherwise falls back
 * to a dash. Mirrors the absolute-layer rules.
 */
const counterpartyLabel = (order: MockOrder): { primary: string; sub: string } => {
  if (order.client && !order.client.isInternal) {
    return { primary: order.client.name, sub: 'On behalf of' };
  }
  if (order.carrier) {
    return { primary: order.carrier.name, sub: 'Carrier' };
  }
  return { primary: 'Internal', sub: 'No external party' };
};

/**
 * Financing-style compact table: tight rows, multi-line cells, and
 * a single chevron column on the trailing edge that toggles a
 * full-width expanded row beneath the row.
 *
 * The expanded row is rendered inside its own `<tr>` with a single
 * `<td colSpan={N}>`, which is the standard semantic-HTML way to
 * embed a sub-component without breaking the parent table grid.
 */
export const MockOrdersTable: React.FC<MockOrdersTableProps> = ({
  orders,
  viewer,
}) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const COL_COUNT = 7;

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-gray-100">
        <thead className="bg-gray-50">
          <tr className="text-start text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            <th className="px-4 py-3 text-start">Reference</th>
            <th className="px-4 py-3 text-start">Route</th>
            <th className="px-4 py-3 text-start">Counterparty</th>
            <th className="px-4 py-3 text-end">Price</th>
            <th className="px-4 py-3 text-start">Driver</th>
            <th className="px-4 py-3 text-start">Status</th>
            <th className="px-4 py-3 w-10" aria-label="Expand" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {orders.map(order => {
            const isOpen = expanded.has(order.id);
            const counterparty = counterpartyLabel(order);
            const status = statusPill[order.status];
            return (
              <React.Fragment key={order.id}>
                <tr
                  className={`group cursor-pointer transition-colors ${
                    isOpen ? 'bg-gray-50/80' : 'hover:bg-gray-50/60'
                  }`}
                  onClick={() => toggle(order.id)}>
                  <td className="px-4 py-3 align-top">
                    <div className="text-[13px] font-semibold text-gray-900">
                      {order.ref}
                    </div>
                    <div className="text-[11px] text-gray-500">
                      {formatDate(order.createdAt)}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="text-[13px] font-medium text-gray-900">
                      {order.pickup.city}
                      <span className="mx-1.5 text-gray-300">→</span>
                      {order.dropoff.city}
                    </div>
                    <div className="text-[11px] text-gray-500">
                      Pickup {formatDate(order.pickup.date)}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="text-[13px] font-medium text-gray-900 truncate max-w-[180px]">
                      {counterparty.primary}
                    </div>
                    <div className="text-[11px] text-gray-500">
                      {counterparty.sub}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top text-end">
                    <div className="text-[13px] font-semibold tabular-nums text-gray-900">
                      {pickHeadlineAmount(order)}
                    </div>
                    {order.legs.length > 1 && (
                      <div className="text-[11px] text-gray-500">
                        {order.legs.length} legs
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    {order.driver ? (
                      <div>
                        <div className="text-[13px] font-medium text-gray-900 truncate max-w-[140px]">
                          {order.driver.name}
                        </div>
                        <div className="text-[11px] text-gray-500 tabular-nums">
                          {order.driver.phone}
                        </div>
                      </div>
                    ) : order.status === 'searching' ? (
                      <div className="inline-flex items-center gap-1.5 text-[12px] text-blue-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
                        Searching
                      </div>
                    ) : (
                      <span className="text-[12px] text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${status.className}`}>
                      {status.label}
                    </span>
                  </td>
                  <td className="px-2 py-3 text-end align-top">
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      aria-label={isOpen ? 'Collapse row' : 'Expand row'}
                      onClick={e => {
                        e.stopPropagation();
                        toggle(order.id);
                      }}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                      <ChevronDownIcon
                        className={`size-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                  </td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={COL_COUNT} className="p-0">
                      <ExpandedOrderRow order={order} viewer={viewer} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
